import { IllegalMoveError, type ChessAdapter } from '@chessmate/game-engine';
import type { PositionSnapshot, San, Turn } from '@chessmate/protocol';
import type { DrillStep } from './schema.js';

export interface DrillCheck {
  readonly pass: boolean;
  readonly feedback: string;
  readonly san?: San;
}

export interface DrillContext {
  readonly adapter: ChessAdapter;
  readonly pos: PositionSnapshot;
  readonly step: DrillStep;
  readonly turn: Turn;
}

/** Eval delta from the mover's perspective: positive = the mover gained. */
function deltaFor(turn: Turn, before: number, after: number): number {
  return turn === 'w' ? after - before : before - after;
}

/**
 * Eval delta in centipawns from the mover's perspective. `adapter.evaluate`
 * returns pawn units (1 pawn = 1.0), while drill `tolerance` is expressed in
 * centipawns — convert before comparing.
 */
function deltaCpFor(turn: Turn, before: number, after: number): number {
  return deltaFor(turn, before, after) * 100;
}

/** Grade a single drill attempt (from → to) against the drill step. */
export function gradeDrill(ctx: DrillContext, from: string, to: string): DrillCheck {
  const { adapter, pos, step, turn } = ctx;

  let next: PositionSnapshot;
  let san: San;
  try {
    next = adapter.makeMove(pos, from, to);
    const last = adapter.history(next).at(-1);
    if (!last) {
      throw new Error('game-engine: makeMove produced no history entry');
    }
    san = last.san;
  } catch (err) {
    if (err instanceof IllegalMoveError) {
      return { pass: false, feedback: step.hint ?? 'That move is not allowed — try again.' };
    }
    throw err;
  }

  switch (step.task) {
    case 'legal-move':
      return { pass: true, feedback: 'Yes! That move is legal.', san };

    case 'trade': {
      // A trade must actually capture something: the destination square held an
      // enemy piece before the move.
      const captured = adapter.history(next).at(-1)?.captured;
      if (!captured) {
        return { pass: false, feedback: 'A trade means capturing a piece. Look for a capture!', san };
      }
      const before = adapter.evaluate(pos);
      const after = adapter.evaluate(next);
      const pass = deltaCpFor(turn, before, after) >= -(step.tolerance ?? 150);
      return {
        pass,
        feedback: pass ? 'Nice trade — you came out ahead!' : 'Hmm, that trade loses material. Try again.',
        san,
      };
    }

    case 'best-move': {
      const tolerance = step.tolerance ?? 150;
      if (step.answer && step.answer.length > 0) {
        const pass = step.answer.includes(san);
        return {
          pass,
          feedback: pass
            ? 'Great move!'
            : `Good try — there is an even stronger move. ${step.hint ?? 'Look for a capture or a fork.'}`,
          san,
        };
      }
      const before = adapter.evaluate(pos);
      const after = adapter.evaluate(next);
      const pass = deltaCpFor(turn, before, after) >= tolerance;
      return {
        pass,
        feedback: pass ? 'Great move!' : 'There is a better move — keep thinking.',
        san,
      };
    }
  }
}
